<template>
  <div class="profile-container">
    <!-- Header avec image de couverture -->
    <div class="profile-header">
      <div class="cover-image-container">
        <img 
          :src="profileData.bgImage ? `${API_URL}${profileData.bgImage}` : '/assets/images/default-cover.jpg'" 
          alt="Image de couverture" 
          class="cover-image"
        />
        <div class="cover-overlay">
          <button 
            v-if="!isEditMode" 
            @click="triggerCoverUpload" 
            class="change-cover-btn"
          >
            <span class="material-icons">photo_camera</span>
            Changer la couverture
          </button>
        </div>
        <input 
          ref="coverInput" 
          type="file" 
          accept="image/*" 
          @change="handleCoverUpload" 
          style="display: none"
        />
      </div>
      
      <!-- Contenu principal du header -->
      <div class="profile-main-content">
        <!-- Photo de profil -->
        <div class="profile-avatar-container">
          <div class="profile-avatar">
            <img 
              :src="profileData.profile ? `${API_URL}${profileData.profile}` : '/assets/images/default-avatar.png'" 
              alt="Photo de profil" 
              class="avatar-image"
            />
            <button 
              v-if="!isEditMode" 
              @click="triggerProfileUpload" 
              class="change-avatar-btn"
              title="Changer la photo de profil"
            >
              <span class="material-icons">photo_camera</span>
            </button>
          </div>
          <input 
            ref="profileInput" 
            type="file" 
            accept="image/*" 
            @change="handleProfileUpload" 
            style="display: none"
          />
        </div>
        
        <!-- Informations principales -->
        <div class="profile-info">
          <div class="profile-text-content">
            <h1 class="profile-name">{{ profileData.fullName }}</h1>
            <p class="profile-role">{{ profileData.role }}</p>
            <p class="profile-description">{{ profileData.description }}</p>
            <div class="profile-stats">
              <div class="stat-item">
                <span class="material-icons">schedule</span>
                <span>Dernière connexion: {{ formatDate(profileData.lastLogin) }}</span>
              </div>
              <div class="stat-item">
                <span class="material-icons">verified</span>
                <span>{{ profileData.isActive ? 'Actif' : 'Inactif' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Actions -->
      <div class="profile-actions">
        <button 
          v-if="!isEditMode" 
          @click="toggleEditMode" 
          class="edit-btn"
        >
          <span class="material-icons">edit</span>
          Modifier le profil
        </button>
        <div v-else class="edit-actions">
          <button @click="saveProfile" class="save-btn" :disabled="isSaving">
            <span class="material-icons">save</span>
            {{ isSaving ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
          <button @click="cancelEdit" class="cancel-btn">
            <span class="material-icons">close</span>
            Annuler
          </button>
        </div>
      </div>
    </div>
    
    <!-- Contenu du profil -->
    <div class="profile-content">
      <div class="profile-grid">
        <!-- Informations personnelles -->
        <div class="profile-card">
          <div class="card-header">
            <span class="material-icons">person</span>
            <h3>Informations personnelles</h3>
          </div>
          <div class="card-content">
            <div class="info-grid">
              <div class="info-item">
                <label>Prénom</label>
                <input 
                  v-if="isEditMode" 
                  v-model="editData.prenom" 
                  type="text" 
                  class="edit-input"
                />
                <span v-else>{{ profileData.prenom }}</span>
              </div>
              <div class="info-item">
                <label>Nom</label>
                <input 
                  v-if="isEditMode" 
                  v-model="editData.noms" 
                  type="text" 
                  class="edit-input"
                />
                <span v-else>{{ profileData.noms }}</span>
              </div>
              <div class="info-item">
                <label>Sexe</label>
                <select 
                  v-if="isEditMode" 
                  v-model="editData.sexe" 
                  class="edit-select"
                >
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
                <span v-else>{{ profileData.sexe === 'M' ? 'Masculin' : 'Féminin' }}</span>
              </div>
              <div class="info-item">
                <label>Date de naissance</label>
                <input 
                  v-if="isEditMode" 
                  v-model="editData.dateNaissance" 
                  type="date" 
                  class="edit-input"
                />
                <span v-else>{{ formatDate(profileData.dateNaissance) }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Contact -->
        <div class="profile-card">
          <div class="card-header">
            <span class="material-icons">contact_phone</span>
            <h3>Contact</h3>
          </div>
          <div class="card-content">
            <div class="info-grid">
              <div class="info-item">
                <label>Email</label>
                <input 
                  v-if="isEditMode" 
                  v-model="editData.email" 
                  type="email" 
                  class="edit-input"
                />
                <span v-else>{{ profileData.email }}</span>
              </div>
              <div class="info-item">
                <label>Téléphone</label>
                <input 
                  v-if="isEditMode" 
                  v-model="editData.telephone" 
                  type="tel" 
                  class="edit-input"
                />
                <span v-else>{{ profileData.telephone }}</span>
              </div>
              <div class="info-item full-width">
                <label>Adresse</label>
                <textarea 
                  v-if="isEditMode" 
                  v-model="editData.adresse" 
                  class="edit-textarea"
                  rows="3"
                ></textarea>
                <span v-else>{{ profileData.adresse }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Description -->
        <div class="profile-card full-width">
          <div class="card-header">
            <span class="material-icons">description</span>
            <h3>Description</h3>
          </div>
          <div class="card-content">
            <div class="info-item">
              <textarea 
                v-if="isEditMode" 
                v-model="editData.description" 
                class="edit-textarea"
                rows="4"
                placeholder="Décrivez-vous en quelques mots..."
              ></textarea>
              <p v-else class="description-text">{{ profileData.description || 'Aucune description disponible' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>Chargement du profil...</p>
    </div>
    
    <!-- Toast notifications -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <span class="material-icons">{{ notification.icon }}</span>
      <span>{{ notification.message }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import api from '@/services/api'

// URL de l'API backend
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000'

// État réactif
const isLoading = ref(true)
const isEditMode = ref(false)
const isSaving = ref(false)
const profileInput = ref(null)
const coverInput = ref(null)

// Données du profil
const profileData = reactive({
  id: null,
  username: '',
  prenom: '',
  noms: '',
  sexe: '',
  description: '',
  email: '',
  telephone: '',
  dateNaissance: '',
  adresse: '',
  profile: '',
  bgImage: '',
  role: '',
  isActive: false,
  lastLogin: '',
  fullName: ''
})

// Données d'édition
const editData = reactive({
  prenom: '',
  noms: '',
  sexe: '',
  description: '',
  email: '',
  telephone: '',
  dateNaissance: '',
  adresse: ''
})

// Notifications
const notification = reactive({
  show: false,
  type: 'success',
  message: '',
  icon: 'check_circle'
})

// Charger le profil utilisateur
const loadProfile = async () => {
  try {
    isLoading.value = true
    const response = await api.get('/api/auth/profile')
    
    if (response.data.success) {
      Object.assign(profileData, response.data.data)
      copyToEditData()
    }
  } catch (error) {
    showNotification('error', 'Erreur lors du chargement du profil', 'error')
  } finally {
    isLoading.value = false
  }
}

// Copier les données vers le formulaire d'édition
const copyToEditData = () => {
  editData.prenom = profileData.prenom
  editData.noms = profileData.noms
  editData.sexe = profileData.sexe
  editData.description = profileData.description
  editData.email = profileData.email
  editData.telephone = profileData.telephone
  editData.dateNaissance = profileData.dateNaissance
  editData.adresse = profileData.adresse
}

// Basculer le mode édition
const toggleEditMode = () => {
  isEditMode.value = true
  copyToEditData()
}

// Annuler l'édition
const cancelEdit = () => {
  isEditMode.value = false
  copyToEditData()
}

// Sauvegarder le profil
const saveProfile = async () => {
  try {
    isSaving.value = true
    const response = await api.put('/api/auth/profile', editData)
    
    if (response.data.success) {
      Object.assign(profileData, response.data.data)
      isEditMode.value = false
      showNotification('success', 'Profil mis à jour avec succès', 'check_circle')
    }
  } catch (error) {
    const message = error.response?.data?.message || 'Erreur lors de la mise à jour'
    showNotification('error', message, 'error')
  } finally {
    isSaving.value = false
  }
}

// Upload photo de profil
const triggerProfileUpload = () => {
  profileInput.value.click()
}

const handleProfileUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  const formData = new FormData()
  formData.append('profile', file)
  
  try {
    showNotification('info', 'Upload en cours...', 'cloud_upload')
    const response = await api.post('/api/auth/upload-profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    if (response.data.success) {
      profileData.profile = response.data.data.profileImage
      showNotification('success', 'Photo de profil mise à jour', 'check_circle')
      // Réinitialiser l'input pour permettre le même fichier
      profileInput.value.value = ''
    }
  } catch (error) {
    console.error('Erreur upload profil:', error)
    const message = error.response?.data?.message || 'Erreur lors de l\'upload'
    showNotification('error', message, 'error')
  }
}

// Upload image de couverture
const triggerCoverUpload = () => {
  coverInput.value.click()
}

const handleCoverUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  const formData = new FormData()
  formData.append('cover', file)
  
  try {
    const response = await api.post('/api/auth/upload-cover-image', formData)
    if (response.data.success) {
      profileData.bgImage = response.data.data.coverImage
      showNotification('success', 'Image de couverture mise à jour', 'check_circle')
    }
  } catch (error) {
    const message = error.response?.data?.message || 'Erreur lors de l\'upload'
    showNotification('error', message, 'error')
  }
}

// Afficher une notification
const showNotification = (type, message, icon) => {
  notification.type = type
  notification.message = message
  notification.icon = icon
  notification.show = true
  
  setTimeout(() => {
    notification.show = false
  }, 4000)
}

// Formater une date
const formatDate = (dateString) => {
  if (!dateString) return 'Non défini'
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Charger le profil au montage
onMounted(() => {
  loadProfile()
})
</script>

<style scoped>
@import '@/assets/css/variables.css';

.profile-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
}

.profile-header {
  position: relative;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.cover-image-container {
  position: relative;
  height: 300px;
  overflow: hidden;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 100%);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 20px;
}

.change-cover-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--turquoise-dark);
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.change-cover-btn:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.profile-main-content {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  padding: 40px;
  position: relative;
  z-index: 10;
}

.profile-avatar-container {
  flex-shrink: 0;
}

.profile-avatar {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-image {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  object-fit: cover;
  border: 5px solid white;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
  display: block;
  transition: transform 0.3s ease;
}

.avatar-image:hover {
  transform: scale(1.05);
}

.change-avatar-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--turquoise-primary);
  border: 3px solid white;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(32, 211, 208, 0.4);
}

.change-avatar-btn:hover {
  background: var(--turquoise-dark);
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(32, 211, 208, 0.6);
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-text-content {
  padding-top: 20px;
}

.profile-name {
  font-size: 2.8rem;
  font-weight: 700;
  color: var(--turquoise-very-dark);
  margin: 0 0 12px;
  line-height: 1.2;
}

.profile-role {
  font-size: 1.3rem;
  color: var(--turquoise-primary);
  font-weight: 600;
  margin: 0 0 16px;
  display: inline-block;
  background: linear-gradient(135deg, var(--turquoise-light), var(--turquoise-primary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.profile-description {
  font-size: 1.1rem;
  color: #64748b;
  margin: 0 0 28px;
  line-height: 1.7;
  max-width: 600px;
}

.profile-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  margin-bottom: 32px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.7);
  padding: 12px 16px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.stat-item .material-icons {
  font-size: 20px;
  color: var(--turquoise-primary);
}

.profile-actions {
  position: absolute;
  top: 40px;
  right: 40px;
  z-index: 15;
}

.edit-btn, .save-btn, .cancel-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.edit-btn {
  background: var(--turquoise-primary);
  color: white;
}

.edit-btn:hover {
  background: var(--turquoise-dark);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(32, 211, 208, 0.3);
}

.edit-actions {
  display: flex;
  gap: 12px;
}

.save-btn {
  background: #10b981;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-2px);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-btn {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.cancel-btn:hover {
  background: #e2e8f0;
}

.profile-content {
  padding: 40px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.profile-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.profile-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.profile-card.full-width {
  grid-column: 1 / -1;
}

.card-header {
  background: linear-gradient(135deg, var(--turquoise-primary), var(--turquoise-dark));
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.card-content {
  padding: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-weight: 600;
  color: var(--turquoise-very-dark);
  font-size: 0.9rem;
}

.info-item span {
  color: #64748b;
  font-size: 1rem;
}

.edit-input, .edit-select, .edit-textarea {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.edit-input:focus, .edit-select:focus, .edit-textarea:focus {
  outline: none;
  border-color: var(--turquoise-primary);
  box-shadow: 0 0 0 3px rgba(32, 211, 208, 0.1);
}

.edit-textarea {
  resize: vertical;
  min-height: 80px;
}

.description-text {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid var(--turquoise-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  z-index: 1001;
  animation: slideIn 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.notification.success {
  background: #10b981;
  color: white;
}

.notification.error {
  background: #ef4444;
  color: white;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .profile-grid {
    grid-template-columns: 1fr;
    padding: 0 20px;
  }
  
  .profile-main-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;
    gap: 24px;
  }
  
  .profile-actions {
    position: static;
    margin-top: 20px;
    align-self: stretch;
  }
  
  .profile-name {
    font-size: 2.2rem;
  }
  
  .profile-role {
    font-size: 1.1rem;
  }
  
  .cover-image-container {
    height: 200px;
  }
  
  .profile-avatar {
    width: 120px;
    height: 120px;
  }
  
  .avatar-image {
    width: 120px;
    height: 120px;
  }
  
  .profile-stats {
    justify-content: center;
    flex-direction: column;
    gap: 16px;
  }
  
  .stat-item {
    justify-content: center;
  }
}
</style>
